import { EmbedContextOptions } from '@thoughtspot/blink-context';
import _ from 'lodash';
import { shouldHideTabForEmbed } from '/@utils/embed.util';
import {
    ApolloCache,
    ApolloClient,
    gql,
    Reference,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    LoadingState,
    LogicalTableTypeEnum,
    Pinboard,
    PinboardEditSession,
    PinboardSourceDetail,
} from '../generated/graphql-types';

export const pinboardQueries = [
    'ActionsAssociation_getCustomActionsAssociation',
    'getAnswer',
    'getPinboardDataSources',
    'getPinboardTopUsersById',
    'getRecentlyPinnedPinboard',
    'getSourceDetailById',
    'getPinboard',
    'getVizData',
    'getPersonalisedViews',
    'getLiveboardInsights',
];

export const PinboardUpdatePropertiesMutation = 'Pinboard__updateProperties';

export function setPinboardLoading(
    client: ApolloClient<object>,
    pinboardSessionId: string,
    loadingState: LoadingState,
) {
    const pinboardEditSession = {
        __typename: 'PinboardEditSession',
        id: {
            sessionId: pinboardSessionId,
        },
    };
    const pinboardSession: PinboardEditSession = client.cache.readQuery({
        id: client.cache.identify(pinboardEditSession),
        query: gql`
            {
                pinboard {
                    id
                }
            }
        `,
    });
    try {
        client.cache.writeFragment({
            id: client.cache.identify(pinboardSession.pinboard),
            fragment: gql`
                fragment isLoading on Pinboard {
                    pinboardLoading @client
                }
            `,
            data: {
                pinboardLoading: loadingState,
            },
        });
    } catch (error) {
        if (error?.message?.includes('already recomputing')) {
            // Suppress "already recomputing" error to prevent crashes
            return;
        }
        // Re-throw any other errors
        throw error;
    }
}

export const setPinboardMutationInProgress = (
    client: ApolloClient<object>,
    pinboardSessionId: string,
    isMutationInProgress: boolean,
) => {
    const bachPinboardSession = {
        __typename: 'BachPinboardSession',
        sessionId: pinboardSessionId,
    };
    const currentValue: any = client.cache.readQuery({
        id: client.cache.identify(bachPinboardSession),
        query: gql`
            {
                isMutationInProgress @client
            }
        `,
    });
    if (currentValue?.isMutationInProgress !== isMutationInProgress) {
        try {
            client.cache.writeFragment({
                id: client.cache.identify(bachPinboardSession),
                fragment: gql`
                    fragment isMutationInProgress on BachPinboardSession {
                        isMutationInProgress @client
                    }
                `,
                data: {
                    isMutationInProgress,
                },
            });
        } catch (error) {
            if (error?.message?.includes('already recomputing')) {
                // Suppress "already recomputing" error to prevent crashes
                return;
            }
            // Re-throw any other errors
            throw error;
        }
    }
};

// this will remove the PinboardSourceDetail from the cache for the given dataSourceId
export const deletePinboardDataSourceFromCache = (
    cache: ApolloCache<any>,
    dataSourceId: string,
) => {
    cache.evict({
        id: cache.identify({
            id: dataSourceId,
            __typename: 'PinboardSourceDetail',
        }),
        broadcast: true,
    });
};

export enum PinboardURLValidationErrorType {
    TAB_ID_MISSING = 'TAB_ID_MISSING',
    VIZ_ID_MISSING = 'VIZ_ID_MISSING',
    VIZ_NOT_IN_TAB = 'VIZ_NOT_IN_TAB',
    PINBOARD_ID_MISMATCH = 'PINBOARD_ID_MISMATCH',
    TAB_ID_REQUIRED = 'TAB_ID_REQUIRED',
}
export interface PinboardURLValidationError {
    type: PinboardURLValidationErrorType;
    blocking: boolean;
}

export const getPinboardURLError = (
    pinboard: Pinboard,
    pinboardId: string,
    tabId: string = undefined,
    vizId: string = undefined,
    embedConfig: EmbedContextOptions = undefined,
): PinboardURLValidationError => {
    if (pinboardId !== pinboard.id) {
        return {
            type: PinboardURLValidationErrorType.PINBOARD_ID_MISMATCH,
            blocking: true,
        };
    }

    const visileOrderedTabIDs = pinboard?.orderedTabIds.filter(
        tabId => !shouldHideTabForEmbed(embedConfig, tabId),
    );

    if (_.isEmpty(tabId) && visileOrderedTabIDs.length > 0) {
        return {
            type: PinboardURLValidationErrorType.TAB_ID_REQUIRED,
            blocking: false,
        };
    }

    if (vizId) {
        const vizIdFromPinboard = pinboard?.containers.find(
            container => container.id === vizId,
        )?.id;

        if (vizIdFromPinboard !== vizId) {
            return {
                type: PinboardURLValidationErrorType.VIZ_ID_MISSING,
                blocking: false,
            };
        }
        const tabIdFromPinboard = pinboard?.tabs?.find(tab =>
            tab.containerIds.includes(vizId),
        )?.id;
        if (tabIdFromPinboard !== tabId) {
            return {
                type: PinboardURLValidationErrorType.VIZ_NOT_IN_TAB,
                blocking: false,
            };
        }
    }

    const doesTabExist =
        _.isEmpty(tabId) || visileOrderedTabIDs?.includes(tabId);

    if (!doesTabExist) {
        return {
            type: PinboardURLValidationErrorType.TAB_ID_MISSING,
            blocking: false,
        };
    }

    return null;
};

export const getValidURLIds = (
    pinboard: Pinboard,
    tabId: string,
    vizId: string,
    embedConfig: EmbedContextOptions = undefined,
): {
    vizIdToLoad: string;
    tabIdToLoad: string;
} => {
    const vizIdToLoad = vizId
        ? pinboard?.containers.find(container => container.id === vizId)?.id
        : null;
    if (vizIdToLoad) {
        const tabIdToLoad = pinboard?.tabs?.find(tab =>
            tab.containerIds.includes(vizIdToLoad),
        )?.id;
        return {
            vizIdToLoad,
            tabIdToLoad,
        };
    }
    const visileOrderedTabIDs = pinboard?.orderedTabIds.filter(
        tabId => !shouldHideTabForEmbed(embedConfig, tabId),
    );
    const tabIdToLoad = visileOrderedTabIDs?.includes(tabId)
        ? tabId
        : visileOrderedTabIDs?.[0];
    return {
        vizIdToLoad,
        tabIdToLoad,
    };
};

export const getFilterableDataSources = _.memoize(
    (dataSources: PinboardSourceDetail[]) => {
        return (
            dataSources?.filter(
                dataSource =>
                    LogicalTableTypeEnum.Worksheet === dataSource.type,
            ) || []
        );
    },
);

export const getOptimisiticResponseForPinboardTitleDescription = (
    client: ApolloClient<Record<string, any>>,
    pinboardSessionId: string,
    pinboardId: string,
    key: 'name' | 'description',
    value: string,
) => {
    const cacheData = client.cache.extract();
    const cachedPinboard =
        cacheData[
            client.cache.identify({
                /* eslint-disable-next-line no-underscore-dangle */
                __typename: 'Pinboard',
                id: pinboardId,
            })
        ];
    const containerRefs = (cachedPinboard?.containers as Reference[]) || [];
    return {
        id: {
            sessionId: pinboardSessionId,
        },
        pinboard: {
            ...(cachedPinboard as Pinboard),
            [key]: value,
            containers: containerRefs.map(container => {
                /* eslint-disable-next-line no-underscore-dangle */
                return cacheData[container.__ref];
            }),
        },
    };
};

export type FormulaEditorState = {
    isLoading?: boolean;
    isCompleted?: boolean;
    isEmpty?: boolean;
    isError?: boolean;
    isBackgroundApiLoading?: boolean;
};

export interface FormulaCodeEditorContainerProps {
    answerSessionId: string;
    setFormulaState: (updatedState: FormulaEditorState) => void;
}
